import { StatsRow } from '@/components/atoms/StatsRow/StatsRow'
import { useTranslation } from 'next-i18next/pages'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  SimpleGrid,
  Center,
  Text,
  Box,
} from '@chakra-ui/react'
import { useQuery } from '@apollo/client'
import { Spinner } from '@/components/atoms/Spinner'
import { EXPLORERS } from '@/constants/constants'
import { CopiableLink } from '@/components/atoms/CopiableLink/CopiableLink'
import { GET_RUNES_GAME } from '@/api/query'
import { Get_Runes_GameQuery, MiniGameStatus } from '@/__generated__/graphql'
import { useEffect } from 'react'
import { UserGameResult } from '../UserGameResult/UserGameResult'
import { FiatOrCryptoAmount } from '@/components/atoms/Amount/Amount'
import { getTokenByDenom } from '@/services/token-service'
import { TokenMoney } from '@ninjablaze/sdk-ts'
import { BlurEffect, GameItem } from '@/components/templates/Runes/GameItem'
import { RUNSES } from '@/components/templates/Runes/utils'

export const RunesGameDetails = ({ onClose, gameId }: { onClose: () => void; gameId: string }) => {
  const { t } = useTranslation()

  return (
    <Modal size={'lg'} isOpen={true} onClose={onClose}>
      <ModalOverlay />
      <ModalContent marginLeft={2.5} marginRight={2.5}>
        <ModalHeader pb={2} fontSize="xl" fontWeight="700">
          {t('common.game-details', { gameId })}
        </ModalHeader>
        <ModalCloseButton onClick={onClose} size="sm" />
        <ModalBody pb={5}>
          <RunesGame gameId={gameId} />
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

const RunesGame = ({ gameId }: { gameId: string }) => {
  const { t } = useTranslation()
  const { data, error, refetch } = useQuery(GET_RUNES_GAME, {
    variables: { id: gameId },
  })

  useEffect(() => {
    refetch()
  }, [])

  if (error) {
    return <Text color={'ninja.200'}>{t('common.error-occurred')}</Text>
  }

  if (!data) {
    return (
      <Center>
        <Spinner />
      </Center>
    )
  }

  const gameDetails = transformRuneGameDetails(data)
  const token = getTokenByDenom(gameDetails.denom!)

  return (
    <>
      <UserGameResult
        address={gameDetails.player!}
        multiplier={(parseInt(gameDetails.payout) / parseInt(gameDetails.bet)).toFixed(2)}
        payout={{ amount: gameDetails.payout, denom: gameDetails.denom! }}
        result={MiniGameStatus[gameDetails.status! as keyof typeof MiniGameStatus]}
      />
      <SimpleGrid columnGap={3} columns={{ base: 2, sm: 2 }}>
        <StatsRow name={t('common.game-status')} value={<Text color={'brand.green'}>{t('common.finished')}</Text>} />
        <StatsRow
          name={t('common.bet')}
          value={
            <FiatOrCryptoAmount
              value={new TokenMoney(gameDetails.bet, token.denom)}
              denom={token.denom}
              wrapperStyles={{ fontSize: 'xs' }}
              iconSize={4}
            />
          }
        />
      </SimpleGrid>
      <SimpleGrid columnGap={3} columns={{ base: 1, sm: 2 }}>
        <StatsRow
          name={t('common.tx-hash')}
          value={
            <CopiableLink
              trimmedLength={12}
              maxLength={32}
              link={`${EXPLORERS.INJECTIVE_TRANSACTIONS}${gameDetails.txHash}/`}
              value={gameDetails.txHash}
            />
          }
        />
        <StatsRow name={t('common.date')} value={<Text>{gameDetails.updated.toLocaleString()}</Text>} />
      </SimpleGrid>
      {!!data.runesGame?.result && <RunesResult result={data.runesGame?.result} />}
    </>
  )
}

const RunesResult = ({ result }: { result: Array<number | null> }) => {
  const fillValue = [
    false,
    ...Array.from({ length: 7 }, (_, item: number) =>
      result.filter((x) => x !== null && x === item).length > 1 ? true : false
    ),
  ]

  return (
    <SimpleGrid columns={5} gap={2}>
      {result
        .filter((item) => item !== null)
        .map((item, id) => (
          <Box
            key={id}
            borderRadius={'24px'}
            boxShadow="0px -3.216px 9.647px rgba(255, 255, 255, 0.20) inset, 0px -9.647px 32.158px rgba(161, 164, 252, 0.32) inset"
          >
            <BlurEffect compact={true} index={id} waiting={false}>
              <GameItem compact={true} item={RUNSES[item + 1]} isActive={fillValue[item + 1]} />
            </BlurEffect>
          </Box>
        ))}
    </SimpleGrid>
  )
}

const transformRuneGameDetails = (data: Get_Runes_GameQuery) => {
  return {
    ...data.runesGame,
    status: data.runesGame?.payout > data.runesGame?.bet ? 'WIN' : 'LOSE',
    updated: new Date(data.runesGame?.created),
    txHash: data.runesGame?.txHash || '',
    rounds: data.runesGame?.result as [],
  }
}
